import { action, computed, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { SearchableUser } from '@/features/iam/users/SearchableUser'
import { type Skill } from '@/features/iam/skills/Skill'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type Id } from '@/infra/id'
import { type SkillTag } from '@/features/iam/skills/SkillTag'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import type {
  FetchMemberFn,
  GetWorkspaceSkillsFn,
  ListSkillsFn,
  UpdateMemberSkillsFn,
} from '@/features/iam'

function getUniqueSkillSet(skills: Skill[]) {
  return new Set(skills.map((skill) => skill.id))
}

export class ManageSkillsListDialogViewModel {
  readonly modal = new ModalViewModel()

  /**
   * Whether to use the passed-in user as the dialog target.
   */
  @observable
  accessor selectedUser: SearchableUser = SearchableUser.none

  /**
   * The list of available skills.
   */
  @observable
  accessor availableSkills: Skill[] = []

  /**
   * The currently selected workspace.
   */
  @observable
  accessor workspace: Workspace = Workspace.none

  /**
   * The currently selected skills.
   */
  @observable
  accessor skills = new Set<Id>()

  /**
   * The currently text to filter skills
   */
  @observable
  accessor searchText = ''

  /**
   * A set of the skills ids marks to be removed
   */
  @observable
  accessor removedSkills = new Set<Id>()

  /**
   * The skills pagination  scroller.
   */
  scroller = new InfiniteScrollProvider<Skill>({
    limit: 500, // default page size
    fetchItems: ({ paging, signal }) => {
      const withRetry = this._createAlertRetryWrapper(
        'Loading skills failed',
        signal,
      )
      return withRetry(() =>
        this._listSkills(
          {
            workspaceId: this.workspace.id,
            after: paging.after,
            limit: paging.limit,
          },
          signal,
        ),
      )
    },
  })

  constructor(
    private readonly _listSkills: ListSkillsFn,
    private readonly _getWorkspaceSkills: GetWorkspaceSkillsFn,
    private readonly _fetchMember: FetchMemberFn,
    private readonly _addMemberSkills: UpdateMemberSkillsFn,
    private readonly _removeMemberSkills: UpdateMemberSkillsFn,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {}

  /**
   * The list of skills filtered(text search)
   */
  @computed
  get filteredSkills() {
    if (!this.searchText) {
      return this.availableSkills
    }

    const search = this.searchText.toLowerCase()
    return this.availableSkills.filter(
      (skill) =>
        skill.name.toLowerCase().includes(search) ||
        skill.description?.toLowerCase().includes(search) ||
        skill.tags.some((tag: SkillTag) => {
          return tag.label?.toLowerCase().includes(search)
        }),
    )
  }

  /**
   * A flag to know if all the skills are checked
   */
  @computed
  get allSelected() {
    return (
      this.filteredSkills.length > 0 &&
      this.skills.size === this.filteredSkills.length
    )
  }

  /**
   * Whether a workspace is selected.
   */
  @computed
  get isWorkspaceSelected() {
    return this.workspace !== Workspace.none
  }

  /**
   * Set the selected user.
   */
  @action.bound
  setSelectedUser(user: SearchableUser) {
    this.selectedUser = user
  }

  /**
   * Set the list of available skills.
   */
  @action.bound
  setAvailableSkills(skills: Skill[]) {
    this.availableSkills = skills
  }

  /**
   * Set the workspace field.
   */
  @action.bound
  setWorkspace(workspace: Workspace) {
    this.workspace = workspace
  }

  /**
   * Set the skills field.
   */
  @action.bound
  setSkills(skills: Skill[]) {
    this.skills = getUniqueSkillSet(skills)
  }

  /**
   * Opens the dialog with the contextual user.
   */
  async showWithUser(
    user: SearchableUser,
    workspace: Workspace,
    signal?: AbortSignal,
  ) {
    runInAction(() => {
      this.workspace = workspace
      this.selectedUser = user
    })

    const withRetry = this._createAlertRetryWrapper(
      'Loading skills failed',
      signal,
    )
    const loaded = await Promise.all([
      withRetry(() =>
        this._fetchMember(
          { workspaceId: workspace.id, userPublicId: user.publicId },
          signal,
        ),
      ),
      withRetry(() =>
        this._listSkills({ workspaceId: workspace.id }, signal),
      ).then(() => this._getWorkspaceSkills(workspace.id)),
      this.scroller.fetch({ signal }),
    ])
      // The retry alert has reported the failure.
      .catch(() => null)

    if (!loaded) {
      return
    }

    const [member, availableSkills] = loaded
    runInAction(() => {
      if (member?.skills.length) {
        this.skills = getUniqueSkillSet(member.skills)
      }
      this.availableSkills = availableSkills
      this.modal.show()
    })
  }

  /**
   * Clears manage skills dialog state.
   */
  @action.bound
  reset() {
    this.setSelectedUser(SearchableUser.none)
    this.setAvailableSkills([])
    this.setWorkspace(Workspace.none)
    this.setSkills([])
    this.setSearchText('')
    this.scroller.reset()
    this.removedSkills.clear()
  }

  @action.bound
  setSearchText(text: string) {
    this.searchText = text
  }

  /**
   * Update the selected user by adding and/or removing the selected skills.
   */
  submit = task.resolved(async () => {
    if (
      this.selectedUser === SearchableUser.none ||
      !this.isWorkspaceSelected
    ) {
      return
    }

    try {
      if (this.removedSkills.size > 0) {
        await this._removeMemberSkills(
          this.workspace,
          this.selectedUser.publicId,
          this.scroller.items.filter((skill) =>
            this.removedSkills.has(skill.id),
          ),
        )
      }
      if (this.skills.size > 0) {
        await this._addMemberSkills(
          this.workspace,
          this.selectedUser.publicId,
          this.scroller.items.filter((skill) => this.skills.has(skill.id)),
        )
      }
    } catch (err) {
      this._createAlertErrorHandler('Updating skills failed')(err)
    }

    this._newAlert()
      .withState('success')
      .saying('Success!', 'Skills updated.')
      .autoDismiss(2500)
      .show()

    this.close()
  })

  /**
   * Mark checked/unchecked skill selected
   */
  @action.bound
  toggleSkill(skill: Skill) {
    if (this.skills.has(skill.id)) {
      this.removedSkills.add(skill.id)
      this.skills.delete(skill.id)
    } else {
      this.removedSkills.delete(skill.id)
      this.skills.add(skill.id)
    }
  }

  /**
   * Mark all skills as checked/unchecked
   */
  @action.bound
  selectAllSkills(checked: boolean) {
    if (checked) {
      this.removedSkills.clear()
      this.setSkills(this.filteredSkills)
    } else {
      this.filteredSkills.forEach((skill) => this.removedSkills.add(skill.id))
      this.setSkills([])
    }
  }

  /**
   * Close the dialog and reset selections to default.
   */
  @action.bound
  close() {
    this.reset()
    this.modal.close()
  }
}
